import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { trackContactSubmit } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      trackContactSubmit();
      toast.success('Thank you! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-sand min-h-screen pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="py-24 px-4 bg-navy">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-heading text-6xl md:text-8xl font-light tracking-tight leading-[0.9] text-gold mb-6">
            Get In Touch
          </h1>
          <p className="font-body text-base md:text-lg text-sand/80 leading-relaxed max-w-2xl mx-auto">
            Have questions? We're here to help plan your perfect safari
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-heading text-2xl md:text-4xl font-normal text-navy mb-8">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-navy/10 p-3 rounded-full">
                      <MapPin size={24} className="text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-body font-semibold text-navy mb-1">Location</h3>
                      <p className="font-body text-foreground/70">
                        TADOBA, Chandrapur,<br />
                        Maharashtra, INDIA
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-navy/10 p-3 rounded-full">
                      <Phone size={24} className="text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-body font-semibold text-navy mb-1">Phone</h3>
                      <p className="font-body text-foreground/70">+91 7776828551</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-navy/10 p-3 rounded-full">
                      <Mail size={24} className="text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-body font-semibold text-navy mb-1">Email</h3>
                      <p className="font-body text-foreground/70">jmdofficials08@gmail.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-navy p-8 rounded-xl">
                <h3 className="font-heading text-xl text-gold mb-4">Office Hours</h3>
                <div className="space-y-2 font-body text-sand/80">
                  <p>Monday - Saturday: 9:00 AM - 6:00 PM</p>
                  <p>Sunday: 10:00 AM - 4:00 PM</p>
                  <p className="text-sm text-sand/60 mt-4">
                    *Peak season: Open all days 8:00 AM - 8:00 PM
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white p-8 rounded-xl shadow-xl"
            >
              <h2 className="font-heading text-2xl md:text-4xl font-normal text-navy mb-6">
                Send us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block font-body text-sm font-medium text-navy mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-body text-sm font-medium text-navy mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-email-input"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block font-body text-sm font-medium text-navy mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    data-testid="contact-subject-input"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md h-12 px-4 font-body"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block font-body text-sm font-medium text-navy mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-white/50 border border-border focus:border-navy focus:ring-1 focus:ring-jungle rounded-md px-4 py-3 font-body resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-600/90 text-white px-8 py-4 rounded-full font-bold tracking-wide transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  {!isSubmitting && <Send size={20} />}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;